import { PolybaseSetting } from './../../dataset/polybase-setting';
import { TabularCopySink } from './tabular.copysink';
import { IModel, IAttribute, IViolation } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { AzureSqlDWTableDataset } from 'app/model/dataset/azure-sql-dw-table.dataset';
import { Owned } from 'app/model/base/named';
import { CopyCommandSetting } from 'app/model/dataset/copy-command-setting';
import {
    CopyMethodOptionValue,
    copyMethodOptionValues
} from 'app/shared/components/copy-method-setting/copy-method-setting.const';
import { sanitizeAndFormatString } from 'app/shared/utils/string.util';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { Model } from 'app/model/backendmodel';
import { CopyActivity } from '../copy.activity';
import {
    ICopySinkWithUpdateMethod,
    ModelConstants,
    UpdateMethod,
    UpdateMethodType
} from 'shell-models-lib';
import { getDisplayName } from 'app/model/pipeline/copysink/copysink.utils';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { CopyJobActivity } from 'app/model/copyjob/copy-job.activity';

@Metamodel.model({
    name: adfModelTypeMap.azureSqlDW.copySinkType,
    display: [],
    referable: true,
    tags: { datasetModel: Metamodel.modelFor(AzureSqlDWTableDataset) }
})
export class SqlDWSink extends TabularCopySink implements ICopySinkWithUpdateMethod {
    public static readonly MODEL: IModel;
    public static readonly ALLOW_POLYBASE: IAttribute;
    public static readonly TABLE_OPTION: IAttribute;
    public static readonly SQL_WRITER_USE_TABLE_LOCK: IAttribute;
    public static readonly DISABLE_METRICS_COLLECTION: IAttribute;
    public static readonly ALLOW_COPY_COMMAND: IAttribute;
    public static readonly COPY_METHOD: IAttribute;
    public static readonly WRITE_BEHAVIOR_INTERIM_SCHEMA_NAME: IAttribute;
    public static readonly WRITE_BEHAVIOR_KEYS: IAttribute;
    public static readonly UPSERT_SETTINGS: IAttribute;

    @Metamodel.attribute({
        display: stringResources.PolybaseSetting_AllowPolybase_Label,
        templatable: true,
        association: 'ALLOW_POLYBASE'
    })
    public allowPolyBase?: boolean;

    @Metamodel.attribute({
        display: stringResources.PolybaseSetting_Polybase_Label,
        type: SqlDWSink.referTypePolybaseSetting
    })
    public polybaseSetting?: PolybaseSetting = __IS_FABRIC_DPE__
        ? undefined
        : Owned.create<PolybaseSetting>(PolybaseSetting.MODEL, this);

    @Metamodel.attribute({
        templatable: true,
        association: 'TABLE_OPTION',
        display: stringResources.Dataset_WriterTableOption
    })
    public tableOption?: string;

    @Metamodel.attribute({
        templatable: true,
        association: 'SQL_WRITER_USE_TABLE_LOCK',
        display: stringResources.Dataset_UseBulkInsertTableLock
    })
    public sqlWriterUseTableLock?: boolean = false;

    @Metamodel.attribute({
        templatable: true,
        association: 'DISABLE_METRICS_COLLECTION',
        display: stringResources.CopySinkDisableMetricsCollectionLabel
    })
    public disableMetricsCollection?: boolean;

    @Metamodel.attribute({
        templatable: true,
        association: 'ALLOW_COPY_COMMAND',
        display: stringResources.CopyCommand_AllowCopyCommand_Label
    })
    public allowCopyCommand?: boolean = true;

    @Metamodel.attribute({
        display: stringResources.CopyCommand,
        type: CopyCommandSetting.MODEL
    })
    public copyCommandSettings?: CopyCommandSetting = Owned.create<CopyCommandSetting>(CopyCommandSetting.MODEL, this);

    @Metamodel.attribute({
        templatable: true,
        association: 'COPY_METHOD',
        display: stringResources.CopyMethod_Label
    })
    public copyMethod?: CopyMethodOptionValue = copyMethodOptionValues.CopyCommand as CopyMethodOptionValue;

    @Metamodel.attribute({
        templatable: true,
        association: 'WRITE_BEHAVIOR_INTERIM_SCHEMA_NAME',
        display: stringResources.SqlStagingSchemaLabel
    })
    public writeBehaviorInterimSchemaName?: string;

    @Metamodel.attribute({
        templatable: true,
        association: 'WRITE_BEHAVIOR_KEYS',
        display: stringResources.DataFlowDatasetKeyColumns,
        accepts: ['Array', 'Object'],
        constraint: {
            validator: SqlDWSink.validateKeyColumnsMapping
        }
    })
    public writeBehaviorKeys?: string[];

    // for backward compatibility where metadata-driven template grouped all upsertSettings in 1
    @Metamodel.attribute({
        templatable: true,
        association: 'UPSERT_SETTINGS'
    })
    public upsertSettings?: Model.WriteBehaviorUpsertOptions;

    public get updateMethod(): UpdateMethodType {
        switch (this.copyMethod) {
            case 'copyCommand':
                return UpdateMethod.Append;
            case 'Upsert':
                return UpdateMethod.Upsert;
            case 'SCD2':
                return UpdateMethod.SCD2;
            default:
                return undefined;
        }
    }

    public set updateMethod(value: UpdateMethodType) {
        switch (value) {
            case UpdateMethod.Append:
                this.copyMethod = 'copyCommand';
                this.allowCopyCommand = true;
                break;
            case UpdateMethod.Upsert:
                this.copyMethod = 'Upsert';
                break;
            case UpdateMethod.SCD2:
                this.copyMethod = 'SCD2';
                break;
            default:
        }
    }

    public get upsertKeyColumns(): string[] {
        return this.writeBehaviorKeys;
    }

    public set upsertKeyColumns(value: string[]) {
        this.writeBehaviorKeys = value;
    }

    public static validateKeyColumnsMapping(sink: SqlDWSink, upsertLabel?: string): IViolation[] {
        if (upsertLabel == null) {
            if (sink.owner != null && Metamodel.modelOf(sink.owner).name === ModelConstants.copyJobActivity) {
                upsertLabel =
                    (sink.owner as CopyJobActivity).updateMethod === UpdateMethod.SCD2
                        ? stringResources.WriteBehavior_Option_SCD2
                        : stringResources.MergeLabel;
            } else {
                upsertLabel = stringResources.WriteBehavior_Option_Upsert;
            }
        }
        const writeBehavior = ValueAdapter.toResource(sink, SqlDWSink.COPY_METHOD);
        const owner: CopyActivity = sink?.owner as CopyActivity;
        if (
            !writeBehavior ||
            (writeBehavior !== copyMethodOptionValues.Upsert &&
                (writeBehavior !== copyMethodOptionValues.SCD2 ||
                    Metamodel.modelOf(owner).name !== ModelConstants.copyJobActivity))
        ) {
            // only check for key columns mapping when certain write behavior is upsert or SCD2 in copy job
            return [];
        }
        const upsertSetting = ValueAdapter.toResource(sink, SqlDWSink.UPSERT_SETTINGS);
        const displayName = getDisplayName(sink);
        if (upsertSetting?.type === 'Expression') {
            // parameterized upsertSetting, no keyColumns
            return [];
        } else if (!sink.writeBehaviorKeys || sink.writeBehaviorKeys.length === 0) {
            return [
                {
                    context: sink,
                    message: () => sanitizeAndFormatString(stringResources.RequiresKeyColumn, upsertLabel, displayName)
                }
            ];
        }
        const violations: IViolation[] = [];
        const uniqueNames: Set<string> = new Set();
        let hasEmptyString = false;
        sink.writeBehaviorKeys.forEach((key) => {
            if (key === '') {
                if (!hasEmptyString) {
                    violations.push({
                        context: sink,
                        message: () => sanitizeAndFormatString(stringResources.RequiresKeyColumn, upsertLabel, displayName)
                    });
                    hasEmptyString = true;
                }
            } else if (uniqueNames.has(key)) {
                violations.push({
                    context: sink,
                    message: () => sanitizeAndFormatString(stringResources.DupKeyColumn, key)
                });
            } else {
                uniqueNames.add(key);
            }
        });
        return violations;
    }

    protected static referTypePolybaseSetting(): IModel {
        return PolybaseSetting.MODEL;
    }
}
